import React, { useState, useEffect } from 'react';
import RichTextEditor from './RichTextEditor';
import EmailTemplateManager from '../EmailTemplateManager';
import RecipientSelector from './RecipientSelector';
import { EmailAudience } from '../../api/client';

export interface EnhancedEmailData {
  subject: string;
  body: string;  // Now HTML content
  includeTicketInfo: boolean;
  includeAttendanceButtons?: boolean;
  recipientIds?: number[];
  useTemplateId?: number;
}

interface Template {
  id: number;
  name: string;
  category: string;
  subject: string;
  body: string;
  variables_used: string[];
}

interface Variable {
  name: string;
  description: string;
}

interface EnhancedEmailModalProps {
  eventId: number;
  eventTitle: string;
  audience?: EmailAudience;
  recipients: Array<{ id: number; name: string; email: string; status?: string; is_approved?: boolean; company?: string; relationship_type?: string; reconfirmed_at?: string; selected?: boolean }>;
  onSubmit: (data: EnhancedEmailData) => void;
  onClose: () => void;
  sending: boolean;
}

const EnhancedEmailModal: React.FC<EnhancedEmailModalProps> = ({
  eventId,
  eventTitle,
  audience = 'attendees',
  recipients,
  onSubmit,
  onClose,
  sending
}) => {
  const isInvitees = audience === 'invitees';
  const [activeTab, setActiveTab] = useState<'recipients' | 'compose' | 'preview'>('recipients');
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<number[]>(
    recipients.filter(r => r.selected).map(r => r.id)
  );
  const [emailData, setEmailData] = useState<EnhancedEmailData>({
    subject: '',
    body: '',
    includeTicketInfo: true,
    includeAttendanceButtons: false,
    recipientIds: []
  });
  
  const [templates, setTemplates] = useState<Template[]>([]);
  const [variables, setVariables] = useState<{ [key: string]: Variable[] }>({});
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [messageTab, setMessageTab] = useState<'edit' | 'source'>('edit');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Save what's in the composer as a reusable template (mirrors the invitation sender)
  const saveAsTemplate = async () => {
    if (!emailData.subject || !emailData.body) {
      alert('Write a subject and message first.');
      return;
    }
    const name = window.prompt('Template name:', emailData.subject);
    if (!name) return;
    setSavingTemplate(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/email-templates', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          category: isInvitees ? 'invitation' : 'general',
          subject: emailData.subject,
          body: emailData.body,
          is_active: true
        })
      });
      if (!response.ok) throw new Error((await response.json()).detail || 'Failed to save template');
      const saved = await response.json();
      await loadTemplates();
      setSelectedTemplate(saved.id);
      alert(`Saved as template "${name}".`);
    } catch (err: any) {
      alert(err.message || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };
  const [copiedVar, setCopiedVar] = useState<string | null>(null);

  // Load templates and variables on mount
  useEffect(() => {
    loadTemplates();
    loadVariables();
  }, []);

  // Recipients arrive asynchronously after the modal opens; apply the default
  // selection once they land if the user hasn't picked anyone yet.
  useEffect(() => {
    if (selectedRecipientIds.length === 0 && recipients.length > 0) {
      setSelectedRecipientIds(recipients.filter(r => r.selected).map(r => r.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipients]);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/email-templates', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      setTemplates([]);
    }
  };

  const loadVariables = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/email-templates/variables/list', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setVariables(data || {});
      }
    } catch (error) {
      console.error('Failed to load variables:', error);
      setVariables({});
    }
  };

  const handleTemplateSelect = (templateId: number) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setEmailData({
        ...emailData,
        subject: template.subject,
        body: template.body,
        useTemplateId: templateId
      });
      setSelectedTemplate(templateId);
    }
  };

  const copyVariable = async (variable: string) => {
    const varText = `{{${variable}}}`;
    try {
      await navigator.clipboard.writeText(varText);
      setCopiedVar(variable);
      setTimeout(() => setCopiedVar(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = varText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedVar(variable);
      setTimeout(() => setCopiedVar(null), 2000);
    }
  };

  const handlePreview = async () => {
    setLoadingPreview(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/events/${eventId}/email-preview`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subject: emailData.subject,
          body: emailData.body,
          include_ticket_info: emailData.includeTicketInfo,
          sample_recipient_id: selectedRecipientIds[0] || null,
          audience,
          include_attendance_buttons: !!emailData.includeAttendanceButtons
        })
      });
      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.html);
        setActiveTab('preview');
      } else {
        alert('Failed to generate preview');
      }
    } catch (error) {
      console.error('Failed to generate preview:', error);
      alert('Failed to generate preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...emailData,
      recipientIds: selectedRecipientIds
    });
  };

  const canProceedToCompose = selectedRecipientIds.length > 0;
  const canProceedToPreview = canProceedToCompose && emailData.subject && emailData.body;

  const selectedCount = selectedRecipientIds.length;

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold">
            {isInvitees ? 'Email Invitees' : 'Email Attendees'}
            <span className="text-sm font-normal text-gray-500 ml-2">
              ({selectedCount} recipients)
            </span>
          </h2>
          {isInvitees && (
            <p className="mt-1 text-sm text-gray-600">
              Goes to people who were invited but haven't responded yet. Every email carries that person's own
              Accept / Decline buttons, so this works as a reminder or a resend of the invitation.
            </p>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex">
            <button
              type="button"
              onClick={() => setActiveTab('recipients')}
              className={`px-6 py-3 font-medium flex items-center ${
                activeTab === 'recipients'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="mr-2">Recipients</span>
              <span className="px-2 py-0.5 text-xs bg-gray-200 rounded-full">
                {selectedRecipientIds.length}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('compose')}
              disabled={!canProceedToCompose}
              className={`px-6 py-3 font-medium ${
                activeTab === 'compose'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : canProceedToCompose 
                    ? 'text-gray-500 hover:text-gray-700'
                    : 'text-gray-400 cursor-not-allowed'
              }`}
            >
              Compose
            </button>
            <button
              type="button"
              onClick={() => {
                if (canProceedToPreview) {
                  handlePreview();
                }
              }}
              disabled={!canProceedToPreview || loadingPreview}
              className={`px-6 py-3 font-medium ${
                activeTab === 'preview'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : canProceedToPreview
                    ? 'text-gray-500 hover:text-gray-700'
                    : 'text-gray-400 cursor-not-allowed'
              }`}
            >
              {loadingPreview ? 'Loading...' : 'Preview'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'recipients' ? (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select Recipients</h3>
                <p className="text-sm text-gray-600">
                  {isInvitees
                    ? 'Choose who will receive this email. You can filter by whether they have opened their invitation, by relationship type, or search for specific people.'
                    : 'Choose who will receive this email. You can filter by status, relationship type, or search for specific attendees.'}
                </p>
              </div>
              <RecipientSelector
                mode={audience}
                registrations={recipients.map(r => (isInvitees ? {
                  ...r,
                  is_approved: false,
                  status: r.status || 'invited'
                } : {
                  ...r,
                  is_approved: true,  // Attendee list is already filtered to approved recipients
                  status: r.status || 'approved'
                }) as any)}
                selectedIds={selectedRecipientIds}
                onSelectionChange={setSelectedRecipientIds}
              />
            </div>
          ) : activeTab === 'compose' ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Template selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template (Optional)
                </label>
                <div className="flex gap-2">
                  <select
                    value={selectedTemplate || ''}
                    onChange={(e) => handleTemplateSelect(Number(e.target.value))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Write from scratch, or pick a template --</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.category})
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowTemplateManager(true)}
                    className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm font-medium whitespace-nowrap"
                    title="Create, edit or delete templates"
                  >
                    📑 Templates
                  </button>
                  <button
                    type="button"
                    onClick={saveAsTemplate}
                    disabled={savingTemplate || !emailData.subject || !emailData.body}
                    className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm font-medium whitespace-nowrap disabled:opacity-50"
                    title="Save the current subject and message as a template"
                  >
                    {savingTemplate ? 'Saving…' : '💾 Save as template'}
                  </button>
                </div>
              </div>

              {/* Variables helper */}
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-md">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-gray-700">
                    Template Variables
                  </p>
                  <p className="text-xs text-gray-600">
                    Click to copy, then paste in your message
                  </p>
                </div>
                <div className="space-y-2">
                  {Object.entries(variables).map(([category, vars]) => (
                    <div key={category}>
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                        {category}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {Array.isArray(vars) && vars.map(v => (
                          <button
                            key={v.name}
                            type="button"
                            onClick={() => copyVariable(v.name)}
                            className={`
                              inline-flex items-center px-2 py-1 text-xs font-mono
                              bg-white border rounded-md transition-all duration-200
                              ${copiedVar === v.name 
                                ? 'bg-green-50 border-green-300 text-green-700' 
                                : 'border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                              }
                            `}
                            title={`${v.description} - Click to copy`}
                          >
                            {copiedVar === v.name ? (
                              <>
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Copied!
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3 mr-1 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                {`{{${v.name}}}`}
                              </>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {Object.keys(variables).length === 0 && (
                  <p className="text-xs text-gray-500 italic">Loading variables...</p>
                )}
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  id="subject"
                  type="text"
                  required
                  value={emailData.subject}
                  onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                  placeholder={isInvitees ? `Reminder: you're invited to ${eventTitle}` : `Update about ${eventTitle}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Rich text editor */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Message
                  </label>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setMessageTab('edit')}
                      className={`px-3 py-1 text-xs font-medium rounded ${messageTab === 'edit' ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                      Rich Text
                    </button>
                    <button
                      type="button"
                      onClick={() => setMessageTab('source')}
                      className={`px-3 py-1 text-xs font-medium rounded ${messageTab === 'source' ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                      Source
                    </button>
                  </div>
                </div>
                {messageTab === 'edit' ? (
                  <RichTextEditor
                    content={emailData.body}
                    onChange={(html) => setEmailData({ ...emailData, body: html })}
                    placeholder="Compose your email message..."
                  />
                ) : (
                  <textarea
                    value={emailData.body}
                    onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono text-sm"
                    placeholder="<p>Hi {{given_name}},</p>..."
                  />
                )}
              </div>

              {/* Include ticket info checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="includeTicketInfo"
                  checked={emailData.includeTicketInfo}
                  onChange={(e) => setEmailData({ ...emailData, includeTicketInfo: e.target.checked })}
                  className="h-4 w-4 text-blue-600 rounded"
                />
                <label htmlFor="includeTicketInfo" className="ml-2 text-sm text-gray-700">
                  {isInvitees ? 'Include event details (date, time, venue)' : 'Include event details and ticket QR codes'}
                </label>
              </div>

              {!isInvitees && (
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="includeAttendanceButtons"
                    checked={!!emailData.includeAttendanceButtons}
                    onChange={(e) => setEmailData({ ...emailData, includeAttendanceButtons: e.target.checked })}
                    className="h-4 w-4 mt-0.5 text-blue-600 rounded"
                  />
                  <label htmlFor="includeAttendanceButtons" className="ml-2 text-sm text-gray-700">
                    Include <span className="font-medium">I'm coming</span> / <span className="font-medium">I can't make it</span> buttons
                    <span className="block text-xs text-gray-500">
                      Personal one-click links. "I'm coming" marks them as confirmed; "I can't make it" asks them to confirm, then releases their ticket.
                    </span>
                  </label>
                </div>
              )}
            </form>
          ) : (
            <div className="bg-gray-100 p-4 rounded-lg">
              <iframe
                srcDoc={previewHtml}
                className="w-full h-[500px] bg-white rounded border border-gray-300"
                title="Email Preview"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <div className="flex gap-3">
            {activeTab === 'recipients' && (
              <button
                type="button"
                onClick={() => setActiveTab('compose')}
                disabled={!canProceedToCompose}
                className={`px-4 py-2 rounded-md ${
                  canProceedToCompose
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Continue to Compose ({selectedRecipientIds.length} selected)
              </button>
            )}
            {activeTab === 'compose' && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveTab('recipients')}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (canProceedToPreview) {
                      handlePreview();
                    }
                  }}
                  disabled={!canProceedToPreview || loadingPreview}
                  className={`px-4 py-2 rounded-md ${
                    canProceedToPreview
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {loadingPreview ? 'Loading...' : 'Preview Email'}
                </button>
              </>
            )}
            {activeTab === 'preview' && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveTab('compose')}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Back to Compose
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={sending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {sending ? 'Sending...' : `Send to ${selectedRecipientIds.length} Recipients`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>

      {showTemplateManager && (
        <EmailTemplateManager
          onClose={() => {
            setShowTemplateManager(false);
            loadTemplates();
          }}
          onTemplateSelect={(id) => {
            setShowTemplateManager(false);
            loadTemplates().then(() => handleTemplateSelect(id));
          }}
        />
      )}
    </>
  );
};

export default EnhancedEmailModal;